<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div :class="panelMode ? 'p-3' :'container mt-4 mb-4'">
    <div class="mt-2" style="max-width:597px">
        <h3 style="padding-right: 30px">Tilbehørsbibliotek</h3>
        <div class="mt-2 text-muted" style="padding-right: 30px">
          Gjenbrukbare valg og tillegg. Når du endrer navn eller pris her, oppdateres valget automatisk i alle gruppene som bruker det.
        </div>

        <!-- 1px sentinel: when it scrolls out of view, the header below is stuck to the top -->
        <div ref="stickySentinel" style="height:1px"></div>
        <div class="bo_categories-header bo_panel-sticky-header d-flex align-items-center flex-wrap mt-4">
          <span class="element-title">I biblioteket <span class="bo_count">({{ addOnTemplates.length }})</span></span>
          <span class="ms-auto">
            <button type="button" class="bo_btn" @click="goToCreateTemplatePage"> + Legg til </button>
          </span>
        </div>

        <div v-if="addOnTemplates.length === 0" class="text-muted">Biblioteket er tomt.</div>
        <ul v-else class="bo_categoriesList">
          <li v-for="addOnTemplate in addOnTemplates" :key="addOnTemplate.id" class="addOnTemplate-row">
            <div class="d-flex align-items-center">
              <a class="icon-btn bo_remove-action me-2" @click="removeAddOnTemplate(addOnTemplate)">
                <img src="@/assets/images/remove.png" width="21" title="Slett fra biblioteket"/>
              </a>
              <div class="flex-grow-1">
                <div class="bold">
                  {{ addOnTemplate.name }}
                  <span v-if="addOnTemplate.itemId" class="ms-1" title="Koblet til produkt i menyen">
                    <font-awesome-icon :icon="['fas', 'link']"/>
                  </span>
                </div>
                <div>
                  <span class="price">kr {{ getPriceWithVat(addOnTemplate.price) }} </span>
                  <span> (med {{vat.takeOut}}% mva for takeaway)</span>
                </div>
                <div v-if="useFunctionality === 'POS' || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'">
                  <span class="price">kr {{ getPriceDineInWithVat(addOnTemplate.priceDineIn) }} </span>
                  <span> (med {{vat.dineIn}}% mva for spise her)</span>
                </div>
                <div class="bo_count">{{ getUsageText(addOnTemplate.id) }}</div>
              </div>
              <div class="text-nowrap ms-2">
                <a class="icon-btn" @click="editAddOnTemplate(addOnTemplate)">
                  <img src="@/assets/images/edit.png" width="21" title="Rediger"/>
                </a>
              </div>
            </div>
          </li>
        </ul>

        <div v-if="suggestions.length > 0" class="mt-4">
          <div class="bo_categories-header d-flex align-items-center">
            <span class="element-title">Forslag fra gruppene dine</span>
          </div>
          <div class="text-muted mb-2">
            Disse valgene har samme navn og pris i flere grupper. Samler du dem i biblioteket, holder det å endre pris ett sted.
          </div>
          <ul class="bo_categoriesList">
            <li v-for="suggestion in suggestions" :key="suggestion.key" class="addOnTemplate-row">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <div class="bold">{{ suggestion.name }}</div>
                  <div>
                    <span class="price">kr {{ getPriceWithVat(suggestion.price) }} </span>
                    <span> (med {{vat.takeOut}}% mva for takeaway)</span>
                  </div>
                  <div v-if="useFunctionality === 'POS' || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'">
                    <span class="price">kr {{ getPriceDineInWithVat(suggestion.priceDineIn) }} </span>
                    <span> (med {{vat.dineIn}}% mva for spise her)</span>
                  </div>
                  <div class="bo_count">Brukes i {{ suggestion.addOnGroupCount }} grupper</div>
                </div>
                <div class="text-nowrap ms-2">
                  <button type="button" class="bo_btn_secondary" @click="importSuggestion(suggestion)"> Samle i biblioteket </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
    </div>
  </div>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from '@/components/other/ConfirmDialogue.vue'
import ProductService from "@/services/product/ProductService";
import ItemUtil from "@/util/ItemUtil";
import MerchantUtil from "@/util/MerchantUtil";

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  props: {
    panelMode: { type: Boolean, default: false },
    vat: { type: Object, default: () => ({}) },
    addOns: { type: Array, default: () => [] },
    initialScrollTopProp: { type: Number, default: 0 } //Restores the scroll position when coming back from a library entry
  },
  emits: ['openPanel', 'refresh', 'headerStuck', 'templateDeleted'],
  data() {
    return {
      loading: true,
      addOnTemplates: []
    };
  },
  mounted() {
    this.fetchAddOnTemplates();

    //Tell the parent when the library header is stuck to the top, so the panel's
    //close button can move out of the way
    if(this.$refs.stickySentinel){
      let scrollContainer = this.$refs.stickySentinel.closest('.side-panel');
      this.stickyObserver = new IntersectionObserver(
          (entries) => {
            this.$emit('headerStuck', !entries[0].isIntersecting);
          },
          { root: scrollContainer }
      );
      this.stickyObserver.observe(this.$refs.stickySentinel);
    }
  },
  beforeUnmount() {
    if(this.stickyObserver){
      this.stickyObserver.disconnect();
    }
  },
  computed: {
    useFunctionality(){
      return MerchantUtil.useFunctionality();
    },
    suggestions(){
      //Group addOns not from the library by identical name and prices.
      //Only suggest when the duplicates are used in more than one group, and never mix differing prices.
      let suggestionsByKey = {};
      for (let i = 0; i < this.addOns.length; i++) {
        let addOn = this.addOns[i];
        if(addOn.templateId){
          continue; //Already from the library
        }
        let key = addOn.name.trim().toLowerCase() + '|' + addOn.price + '|' + addOn.priceDineIn;
        if(!suggestionsByKey[key]){
          suggestionsByKey[key] = {
            key: key,
            name: addOn.name.trim(),
            price: addOn.price,
            priceDineIn: addOn.priceDineIn,
            addOns: [],
            addOnGroupIds: []
          };
        }
        suggestionsByKey[key].addOns.push(addOn);
        if(!suggestionsByKey[key].addOnGroupIds.includes(addOn.addOnGroupId)){
          suggestionsByKey[key].addOnGroupIds.push(addOn.addOnGroupId);
        }
      }

      let suggestions = [];
      for (let key in suggestionsByKey) {
        let suggestion = suggestionsByKey[key];
        if(suggestion.addOnGroupIds.length >= 2){
          suggestion.addOnGroupCount = suggestion.addOnGroupIds.length;
          suggestions.push(suggestion);
        }
      }
      return suggestions;
    }
  },
  methods: {
    fetchAddOnTemplates(){
      ProductService.getAddOnTemplates().then(
          (response) => {
            this.addOnTemplates = response.data;
            this.loading = false;
            this.restoreScrollPosition();
          },
          (error) => {
            console.log(error);
          }
      );
    },
    restoreScrollPosition(){
      if(this.initialScrollTopProp > 0){
        this.$nextTick(() => {
          let scrollContainer = this.$refs.stickySentinel ? this.$refs.stickySentinel.closest('.side-panel') : null;
          if(scrollContainer){
            scrollContainer.scrollTop = this.initialScrollTopProp;
          }
        });
      }
    },
    getPriceWithVat(priceWithoutVat){
      return ItemUtil.getPriceWithVat(priceWithoutVat, this.vat);
    },
    getPriceDineInWithVat(priceWithoutVat){
      return ItemUtil.getPriceDineIn(priceWithoutVat || 0, this.vat);
    },
    getUsageCount(templateId){
      let count = 0;
      for (let i = 0; i < this.addOns.length; i++) {
        if(this.addOns[i].templateId == templateId){
          count++;
        }
      }
      return count;
    },
    getUsageText(templateId){
      let count = this.getUsageCount(templateId);
      if(count === 0){
        return 'Brukes ikke i noen grupper';
      } else if(count === 1){
        return 'Brukes i 1 gruppe';
      } else {
        return 'Brukes i ' + count + ' grupper';
      }
    },
    goToCreateTemplatePage(){
      this.$emit('openPanel', 'addOnTemplateCreate', {});
    },
    importSuggestion(suggestion){
      this.loading = true;

      let merchant = MerchantUtil.getMerchant();
      let addOnTemplate = {
        type: "add-on_template",
        merchant: merchant,
        name: suggestion.name,
        price: suggestion.price,
        priceDineIn: suggestion.priceDineIn
      };
      ProductService.createAddOnTemplate(addOnTemplate).then(
          (response) => {
            let templateId = response.data;
            if(templateId){
              //Link the existing addOns to the new library entry
              let addOnsToLink = suggestion.addOns.map(addOn => ({
                id: addOn.id,
                merchant: merchant,
                templateId: templateId
              }));
              ProductService.editAddOns(addOnsToLink).then(
                  () => {
                    this.fetchAddOnTemplates();
                    this.$emit('refresh'); //Reload addOns so the suggestion disappears
                  },
                  (error) => {
                    console.log(error);
                  }
              );
            } else {
              console.log("Creating addOnTemplate failed");
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    editAddOnTemplate(addOnTemplate){
      this.$emit('openPanel', 'addOnTemplateEdit', { addOnTemplateId: addOnTemplate.id, addOnTemplate: addOnTemplate });
    },
    async removeAddOnTemplate(addOnTemplate){
      let usageCount = this.getUsageCount(addOnTemplate.id);
      let message = 'Vil du slette "' + addOnTemplate.name.trim() + '" fra biblioteket?';
      if(usageCount > 0){
        message += ' Valget blir også slettet fra ' + usageCount + (usageCount === 1 ? ' gruppe' : ' grupper') + ' som bruker det.';
      }
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Slett "' + addOnTemplate.name.trim() + '" fra biblioteket',
        message: message,
        okButton: 'Slett',
        cancelButton: 'Avbryt'
      })
      if(ok) {
        ProductService.deleteAddOnTemplate(addOnTemplate).then(
            (response) => {
              if(response.data != null){
                this.fetchAddOnTemplates();
                //Tell the products page so it refreshes when the library closes — the deleted add-on
                //is also removed from the groups that used it.
                this.$emit('templateDeleted');
              } else {
                console.log("Deleting addOnTemplate failed");
              }
            },
            (error) => {
              console.log(error);
            }
        );
      }
    }
  }
};
</script>
